<template>
  <Map :layers="layers"></Map>
  <div class="legend-group">
    <div v-if="rasterCropsLayer" class="box legend-box">
      <h5>{{rasterCropsLayer.classification.title}}<br/><small class="text-muted">of {{ofText}}</small><br/><small class="text-muted">per grid cell - annual</small></h5>
      <div v-if="(sliderValue <= 30) || !basinsMonthsLayer" class="gradient">
        <div class="bar" :style="{'background-image': 'linear-gradient(#' + rasterCropsLayer.classification.colors[1].toString(16).padStart(6, '0') + ', #' + rasterCropsLayer.classification.colors[0].toString(16).padStart(6, '0') + ')'}"></div>
        <div class="high left">high</div>
        <div class="low left">low</div>
      </div>
    </div>
    <div v-if="basinsMonthsLayer" class="control-group">
      <div class="box legend-box">
        <div class="slider-box">
          <input type="range" v-model="sliderValue" :title="'Set visibility of ' + basinsMonthsLayer.classification.title.toLowerCase()" class="form-range"/>
        </div>
        <h5>{{basinsMonthsLayer.classification.title}}<br/><small class="text-muted">per river basin<span v-if="!hbasins || !blue"> - annual</span></small></h5>
        <div v-if="(sliderValue > 30) || !rasterCropsLayer" class="d-flex flex-column-reverse">
          <div v-for="legend of basinsMonthsLegends" :key="legend.color">
            <span class="color-box" :style="{'background-color': legend.color}"></span>
            <span>&nbsp;{{legend.text}}</span>
          </div>
        </div>
      </div>
      <div v-if="((sliderValue > 30) || !rasterCropsLayer) && hbasins">
        <div class="box radio-box">
          <template v-if="blue">
            <div class="form-check">
              <input type="radio" v-model="annual" :value="true" id="annual" class="form-check-input">
              <label for="annual" class="form-check-label">Annual</label>
            </div>
            <div class="form-check">
              <input type="radio" v-model="annual" :value="false" id="monthly" class="form-check-input">
              <label for="monthly" class="form-check-label">Monthly</label>
            </div>
            <div v-if="annual" class="box btn-group">
              <button v-for="annualType1 of annualTypes" :key="annualType1.short" @click="annualType = annualType1" :title="annualType1.long" class="btn btn-outline-dark" :class="{active: annualType == annualType1}">{{annualType1.short}}</button>
            </div>
            <div v-if="!annual" class="box btn-group">
              <button v-for="month1 of months" :key="month1.short" @click="month = month1" :title="month1.long" class="btn btn-outline-dark" :class="{active: month == month1}">{{month1.short}}</button>
            </div>
          </template>
          <template v-else>
            <div class="form-check">
              <input type="radio" v-model="nitrogen" :value="true" id="nitro" class="form-check-input">
              <label for="nitro" class="form-check-label">Nitrogen</label>
            </div>
            <div class="form-check">
              <input type="radio" v-model="nitrogen" :value="false" id="phosphor" class="form-check-input">
              <label for="phosphor" class="form-check-label">Phosphorus</label>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div v-if="scenariosProcessesLayer" class="box legend-box" style="width: 225px; height: 197px">
      <h5>{{scenariosProcessesLayer.classification.title}}<br/><small class="text-muted">of {{ofText}}</small><br/><small class="text-muted">per business location - annual</small></h5>
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="22 16 48.7 48.7">
        <path :fill="'#' + scenariosProcessesLayer.classification.colors[0].toString(16).padStart(6, '0')" stroke="#fff" stroke-width="0.8" stroke-miterlimit="10" d="M43.216,48.299
          c4.956-2.286,8.397-7.295,8.397-13.111c0-7.972-6.462-14.434-14.434-14.434s-14.434,6.462-14.434,14.434
          c0,6.151,3.848,11.4,9.268,13.479c2.315,0.903,5.296,4.381,2.135,8.465c-0.653,0.767-1.049,1.759-1.049,2.845
          c0,2.426,1.966,4.393,4.393,4.393c2.426,0,4.393-1.967,4.393-4.393c0-1.223-0.5-2.328-1.306-3.124
          C39.088,55.401,37.755,51.035,43.216,48.299z"/>
      </svg>
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="-35.5 -57 123 123">
        <path :fill="'#' + scenariosProcessesLayer.classification.colors[0].toString(16).padStart(6, '0')" stroke="#fff" stroke-width="2" stroke-miterlimit="10" d="M43.216,48.299
          c4.956-2.286,8.397-7.295,8.397-13.111c0-7.972-6.462-14.434-14.434-14.434s-14.434,6.462-14.434,14.434
          c0,6.151,3.848,11.4,9.268,13.479c2.315,0.903,5.296,4.381,2.135,8.465c-0.653,0.767-1.049,1.759-1.049,2.845
          c0,2.426,1.966,4.393,4.393,4.393c2.426,0,4.393-1.967,4.393-4.393c0-1.223-0.5-2.328-1.306-3.124
          C39.088,55.401,37.755,51.035,43.216,48.299z"/>
      </svg>
      <div class="high right">high</div>
      <div class="low right">low or unknown</div>
    </div>
  </div>
</template>

<script>
import { CenterScale, Filter, FocusModel, Geometry, Layer, WKTConverter, defaultFocusModel, windowBoundsModel } from "vue-niney";
import { assessment } from "@/services/assessment-model.js";
import Map from '@/components/shared/Map.vue'
import { getRedString, getSignificantNumberString, getWSString } from "@/services/data-tools.js";
import { getCountry, months, scenarioCountryCrops, scenarioProcesses, serverUrl } from "@/services/entity-model.js";
export default {
  components: {
    Map
  },
  data() {
    return {
      sliderValue: 70,
      annual: true,
      annualTypes: months.filter(month => month.annual),
      annualType: months[12],
      months: months.filter(month => !month.annual),
      month: months[0],
      nitrogen: true,
      scenarioCountryCrops: scenarioCountryCrops,
      scenarioProcesses: scenarioProcesses
    };
  },
  mounted: function() {
    this.zoomToCountriesOrBasins();
  },
  methods: {
    zoomToCountriesOrBasins() {
      let countriesOrBasins = this.countriesOrBasins;
      if (assessment.assessmentType.name == "business") {
        countriesOrBasins = countriesOrBasins.concat(scenarioProcesses);
      }
      if (countriesOrBasins.length > 0) {
        const geometry = new Geometry();
        for (const countryOrBasin of countriesOrBasins) {
            geometry.addChild((new WKTConverter()).wktToGeometry(countryOrBasin.envelope));
        }
        
        const envelope = geometry.getEnvelope();
        const centerX = envelope.minX + envelope.getWidth() / 2;
        const centerY = envelope.minY + envelope.getHeight() / 2;
        const scale = Math.max(envelope.getWidth() / windowBoundsModel.bounds.width, envelope.getHeight() / windowBoundsModel.bounds.height) / defaultFocusModel.centerScale.coordPixFactor * 1.05;
        defaultFocusModel.setCenterScale(new CenterScale(centerX, centerY, scale), FocusModel.IF_REQUIRED_UPPER);
      } else {
        defaultFocusModel.setCenterScale(new CenterScale(0, 3000000, 110936068.18103503));
      }
    },
    getSignificantNumberString(n) {
      if (n >= 99.995) {
        return "100";
      }
      if (n >= 98.95) {
        return getSignificantNumberString(n, 4);
      }
      if (n >= 97.5) {
        return getSignificantNumberString(n, 3);
      }
      return getSignificantNumberString(n, 2);
    }
  },
  computed: {
    ofText() {
      if (assessment.scenario != null) {
        return assessment.scenario.name;
      }
      if ((assessment.crop == null) || (assessment.crop.cropID == 100001)) {
        return "all sectors and crops";
      }
      if (assessment.crop.cropID == 10001) {
        return "agriculture (all crops)";
      }
      if ((assessment.crop.cropID == 10002) || (assessment.crop.cropID == 10003)) {
        return "the " + assessment.crop.name.toLowerCase() + " sector";
      }
      return assessment.crop.name.toLowerCase();
    },
    scenarioHasCountryCrops() {
      return (assessment.scenario != null) && (assessment.scenario.type % 10 == 1);
    },
    countriesOrBasins() {
      if (this.scenarioHasCountryCrops) {
        return this.scenarioCountryCrops.map(scenarioCountryCrop => getCountry(scenarioCountryCrop.countryId));
      }
      return assessment.countriesOrBasins || [];
    },
    hbasins() {
      return (assessment.assessmentType.name == "hbasins") || (assessment.assessmentType.name == "business");
    },
    blue() {
      return assessment.viewColor == "blue";
    },
    rasterCropsLayer() {
      if ((assessment.assessmentType.name == "business") && !this.scenarioHasCountryCrops) {
        return null;
      }

            const classifications = {
                green: {propertyName: "raster_theme(112, ",  title: "Green Water Footprint", numClasses: 6, colors: [0xAAFFAA, 0x006000], numbers: null, gradient: true, discrete: false},
                blue:  {propertyName: "raster_theme(113, ",  title: "Blue Water Footprint", numClasses: 6, colors: [0xB0D9FF, 0x004281], numbers: null, gradient: true, discrete: false},
                grey:  {propertyName: "raster_theme(114, ",  title: "Grey Water Footprint", numClasses: 6, colors: [0xCCCCCC, 0x4D4D4D], numbers: null, gradient: true, discrete: false},
                total: {propertyName: "raster_theme(1008, ", title: "Water Footprint", numClasses: 6, colors: [0xEEEE00, 0xDD0000], numbers: null, gradient: true, discrete: false}
            };
            const layer = new Layer("RasterCrops");
            layer.baseURL = serverUrl + "nessie/raster.jsp";
            layer.styleURL = serverUrl + "nessie/named-layer-sld-4-raster.jsp";
            layer.filterModels = [
              assessment.scenario? { filter: new Filter("app:scenarioCode", assessment.scenario.code) }: null,
                                   { filter: new Filter("app:cropID", assessment.crop? assessment.crop.cropID: 100001) }
              ].filter(notNull => notNull);
            layer.classification = classifications[assessment.viewColor];
            layer.infoConverter = value => {
                if (value == "0.0") {
                    return "<strong>Grid Cell</strong><br/>No grid cell";
                }
                return "<strong>Grid Cell</strong><br/>" + layer.classification.title + "<br/>" + value + " mm/yr";
            };
            if (this.basinsMonthsLayer == null) {
                layer.opacity = () => null;
            } else {
                layer.opacity = () => { return {opacity: 1 - this.sliderValue / 100}; };
            }
            
            return layer;
    },
    basinsMonthsLayer() {
      if (assessment.step.name == "accounting") {
          return null;
      }

      const layer = new Layer((this.hbasins? "Hs5": "") + "BasinsMonths");
      layer.baseURL = serverUrl + "nessie/services";
      layer.styleURL = serverUrl + "nessie/named-layer-sld.jsp";
      if (this.hbasins) {
        if (this.blue) {
          layer.filterModels = [
            { filter: new Filter("app:monthId", this.annual? this.annualType.monthId: this.month.monthId) },
            { filter: new Filter("app:typeId", 105) }
          ];
          if (this.annual && (this.annualType == this.annualTypes[3])) {
            layer.classification = {propertyName: "value", title: "Blue Water Reduction Target", numClasses: null, colors: [0x00BB00, 0xEEEE00, 0xEE8800, 0xEE3300, 0xCC0000, 0x880000, 0x550000], numbers: null, gradient: false, discrete: true, thresholds: [1, 2, 4, 6, 10, 12]};
          } else {
            layer.classification = {propertyName: "value", title: "Blue Water Reduction Target", numClasses: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0, 0.1, 5, 25, 50, 80]};
          }
        } else {  // grey
          layer.filterModels = [
            { filter: new Filter("app:monthId", 101) },
            { filter: new Filter("app:typeId", this.nitrogen ? 6 : 7) }
          ];
          layer.classification = {propertyName: "value", title: "Water Pollution Level", numClasses: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0, 0.5, 1, 1.5, 2, 4]};
        }
        layer.infoConverter = info => {
          if (info.match(/<gml:null>missing<\/gml:null>/)) {
            return "<strong>River Basin</strong><br/>No basin";
          }
          const basinId = info.match(/<app:hs5BasinId>(.*)<\/app:hs5BasinId>/)[1];
          let value = info.match(/<app:value>(.*)<\/app:value>/)[1];
          let month = null;
          if (this.blue) {
            if (this.annual && (this.annualType == this.annualTypes[3])) {
              value = parseInt(value);
              value = value + " month" + ((value != 1) ? "s" : "") + " per year";
            } else {
              value = getRedString(this.getSignificantNumberString(parseFloat(value)));
              month = this.annual? this.annualType.long.toLowerCase(): ("in " + this.month.long);
            }
          } else {  // grey
            value = getWSString(this.getSignificantNumberString(parseFloat(value)));
          }
          return "<strong>River Basin</strong> <small>[" + basinId + "0]</small><br/>" + layer.classification.title + "<br/>" + value + (month ? (" <small>" + month + "</small>") : "");
        };
      } else {
        layer.filterModels = [{ filter: new Filter("app:monthID", 101) }];
        if (this.blue) {
          layer.classification = {propertyName: "blueScarcity", title: "Blue Water Scarcity", numClasses: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0, 0.5, 1, 1.5, 2, 4]};
        } else {  // grey
          layer.classification = {propertyName: "greyWS", title: "Water Pollution Level", numClasses: null, colors: [0x115511, 0xEEEE00, 0xDD0000], numbers: null, gradient: false, discrete: false, thresholds: [0, 0.5, 1, 1.5, 2, 4]};
        }
        layer.infoConverter = info => {
          if (info.match(/<gml:null>missing<\/gml:null>/)) {
            return "<strong>River Basin</strong><br/>No basin";
          }
          const name = info.match(/<app:name>(.*)<\/app:name>/)[1];
          const ws = info.match(new RegExp("<app:" + layer.classification.propertyName + ">(.*)</app:" + layer.classification.propertyName + ">"))[1];
          const wsString = getWSString(this.getSignificantNumberString(parseFloat(ws)));
          return "<strong>" + name + " Basin</strong><br/>" + layer.classification.title + "<br/>" + wsString;
        };
      }
      layer.opacity = () => { return {opacity: this.sliderValue / 100}; };
                
      return layer;
    },
    basinsTilesLayer() {
      if (assessment.step.name == "accounting") {
          return null;
      }

                const layer = new Layer("BasinsTiles");
                layer.baseURL = serverUrl + "tiles/" + (this.hbasins? "h": "") + "basins/";
                
                return layer;
    },
    countriesOrBasinsLayer() {
            if (this.countriesOrBasins.length > 0) {
                const layer = new Layer(assessment.assessmentType.layerName);
                layer.baseURL = serverUrl + "nessie/services";
                layer.styleURL = serverUrl + "nessie/named-layer-sld.jsp";
                const idPropertyName = assessment.assessmentType.idPropertyName;
                const ids = this.countriesOrBasins.map(countryOrBasin => countryOrBasin[idPropertyName]);
                layer.filterModels = [{ filter: new Filter("app:" + idPropertyName, ids.join(":")) }];
                layer.opacity = () => null;
                
                return layer;
            }
            return null;
    },
    scenariosProcessesLayer() {
      if (assessment.assessmentType.name == "business") {
        const classifications = {
          green: {propertyName: "greenWF", title: "Green Water Footprint", numClasses: 5, colors: [0x6eb3a2], numbers: [40, 100], gradient: false, discrete: false},
          blue:  {propertyName: "blueWF",  title: "Blue Water Footprint",  numClasses: 5, colors: [0x349bd1], numbers: [40, 100], gradient: false, discrete: false},
          grey:  {propertyName: "greyWF",  title: "Grey Water Footprint",  numClasses: 5, colors: [0x626262], numbers: [40, 100], gradient: false, discrete: false},
          total: {propertyName: "totalWF", title: "Water Footprint",       numClasses: 5, colors: [0xe45a00], numbers: [40, 100], gradient: false, discrete: false}
        };
        const layer = new Layer("ScenariosProcesses");
        layer.baseURL = serverUrl + "nessie/services";
        layer.styleURL = serverUrl + "nessie/named-layer-sld-2-decode-scpr.jsp";
        layer.filterModels = [{filter: new Filter("app:scenarioCode", assessment.scenario? assessment.scenario.code: "none")}];
        layer.classification = classifications[assessment.viewColor];
        layer.opacity = () => null;

        return layer;
      }
      return null;
    },
    layers() {
      return [this.rasterCropsLayer, this.basinsMonthsLayer, this.basinsTilesLayer, this.countriesOrBasinsLayer, this.scenariosProcessesLayer];
    },
    basinsMonthsLegends() {
      if (this.hbasins && this.blue) {
        if (this.annual && (this.annualType == this.annualTypes[3])) {
          return [
            {color: "#00BB00", text: "0 months per year"},
            {color: "#EEEE00", text: "1 month per year"},
            {color: "#EE8800", text: "≥ 2 months per year"},
            {color: "#EE3300", text: "≥ 4 months per year"},
            {color: "#CC0000", text: "≥ 6 months per year"},
            {color: "#880000", text: "≥ 10 months per year"}
          ];
        }
        return [
          {color: "#5b880b", text: "< " + this.getSignificantNumberString(0.1) + "% " + getRedString(this.getSignificantNumberString(0)).replace(/[0-9.%]+/, "")},
          {color: "#a4bb06", text: "≥ " + getRedString(this.getSignificantNumberString(0.1))},
          {color: "#eeee00", text: "≥ " + getRedString(this.getSignificantNumberString(5))},
          {color: "#e89f00", text: "≥ " + getRedString(this.getSignificantNumberString(25))},
          {color: "#e34f00", text: "≥ " + getRedString(this.getSignificantNumberString(50))},
          {color: "#dd0000", text: "≥ " + getRedString(this.getSignificantNumberString(80))}
        ];
      }
      return [
        {color: "#5b880b", text: "< " + this.getSignificantNumberString(0.5) + " " + getWSString(this.getSignificantNumberString(0)).replace(/[0-9.]+/, "")},
        {color: "#a4bb06", text: "≥ " + getWSString(this.getSignificantNumberString(0.5))},
        {color: "#eeee00", text: "≥ " + getWSString(this.getSignificantNumberString(1))},
        {color: "#e89f00", text: "≥ " + getWSString(this.getSignificantNumberString(1.5))},
        {color: "#e34f00", text: "≥ " + getWSString(this.getSignificantNumberString(2))},
        {color: "#dd0000", text: "≥ " + getWSString(this.getSignificantNumberString(4))}
      ];
    }
  },
  watch: {
    scenarioCountryCrops: {
      handler() {
        this.zoomToCountriesOrBasins();
      },
      deep: true
    },
    scenarioProcesses: {
      handler() {
        this.zoomToCountriesOrBasins();
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.legend-group {
  display: flex;
    position: absolute;
    bottom: 10px;
    left: 10px;
  flex-direction: column-reverse;
  align-items: flex-start;
  gap: 4px;
  pointer-events: none;
  .box {
    border-radius: 7px;
    background-color: rgba(237, 235, 229, 0.9);
    pointer-events: all;
  }
  .legend-box {
    position: relative;
    padding: 15px 13px 10px;
      h5 {
        font-size: 15px;
        font-weight: 700;
      }
      svg {
        position: absolute;
        height: 109px;
      }
    .gradient {
      width: 30px;
      padding: 5px 0 5px 10px;
      .bar {
        height: 125px; 
      }
    }
    .high, .low, .left, .right {
      position: absolute;
      &.high {
        &.left {
          bottom: 123px;
        }
        &.right {
          bottom: 94px;
        }
      }
      &.low {
        &.left {
          bottom: 10px;
        }
        &.right {
          bottom: 34px;
        }
      }
      &.left {
        left: 50px;
      }
      &.right {
        left: 95px;
      }
    }
    .slider-box {
      height: 25px;
      .form-range {
        position: relative;
        top: -3px;
      }
    }
    .color-box {
      width: 20px;
      height: 12px;
      margin-left: 10px;
      display: inline-block;
      opacity: 0.75;
    }
  }
  .control-group {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    .radio-box {
      padding: 10px;
      .btn-group {
        margin-top: 4px;
        .btn {
          font-size: inherit;
        }
      }
    }
  }
}

@mixin track() {
  height: 4px;
  background-color: var(--bs-primary-border-subtle);
}
input {
  &::-webkit-slider-runnable-track { @include track }
  &::-moz-range-track { @include track }
  &::-ms-track { @include track }
}
@mixin thumb() {
  position: relative;
  top: -1px;
  width: 14px;
  height: 14px;
}
input {
  &::-webkit-slider-thumb { @include thumb }
  &::-moz-range-thumb { @include thumb }
  &::-ms-thumb { @include thumb }
}
</style>
